<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { validateEmail } from '../helpers';
import { cmsUrl } from '../variables';
import LoadIndicator from '../components/LoadIndicator';

export default {
    name: 'ContributorPage',
    components: {
        LoadIndicator,
    },
    props: {
        slug: String
    },
    data: () => {
        return {
            formData: {
                name: null,
                email: null,
                message: null,
            },
            validationErrors: [],
            sendError: false,
            sendSuccess: false,
        }
    },
    mounted() {
        this.init(this.slug);
    },
    methods: {
        init(slug) {
            this.$store.dispatch('selectContributorForDetail', slug);
        },
        onSubmit(event) {
            event.preventDefault();

            this.validationErrors = [];
            if (!this.formData.name)
                this.validationErrors.push(this.$t('contactPage.validationName'));
            if (!validateEmail(this.formData.email))
                this.validationErrors.push(this.$t('contactPage.validationEmail'));
            if (!this.formData.message)
                this.validationErrors.push(this.$t('contactPage.validationMessage'));
            if (this.validationErrors.length > 0)
                return;

            axios
                .post(cmsUrl + '/contact-form', {
                    name: this.formData.name,
                    email: this.formData.email,
                    message: this.formData.message,
                    subject: this.$t('contactPage.subject'),
                    contributor_id: this.recipient.id,
                })
                .then(() => {
                    this.sendSuccess = true;
                })
                .catch(() => {
                    this.sendError = true;
                })
        }
    },
    computed: {
        ...mapGetters({
            recipient: 'getSelectedDetailContributor',
            isLoading: 'getContributorsIsLoading',
        }),
    },
    beforeDestroy() {
        this.$store.dispatch('selectContributorForDetail', null);
    },
};
</script>

<template>
    <section class="contact-page" v-on:keyup.esc="onClose()">
        <div class="contact-page__content-wrapper" v-if="!isLoading && recipient">
            <h1 class="contact-page__title page-title" v-html="$t('contactPage.title') + recipient.title.rendered"></h1>
            <p class="contact-page__intro" v-html="$t('contactPage.intro')"></p>
            <form class="contact-page__form" v-if="!sendSuccess">
                <div class="contact-page__form__errors" v-if="validationErrors.length > 0 || sendError">
                    <p class="contact-page__form__send-error" v-if="sendError">{{ $t('contactPage.sendError') }}</p>
                    <p class="contact-page__form__validation-error" v-if="validationErrors.length > 0">{{ $t('contactPage.validationError') }}</p>
                    <ul class="contact-page__form__validation">
                        <li 
                            class="contact-page__form__validation-error__field" 
                            v-for="(err, i) in validationErrors" 
                            v-bind:key="i">
                                {{ err }}
                        </li>
                    </ul>
                </div>

                <div class="contact-page__form__row">
                    <div class="contact-page__form__group form-control form-control--light">
                        <label class="contact-page__form_label form-control__label">{{ $t('contactPage.labelName') }}</label>
                        <input 
                            type="text" 
                            class="contact-page__form__form-control form-control__input" 
                            v-model="formData.name"
                            v-bind:placeholder="$t('contactPage.placeholderName')">
                    </div>

                    <div class="contact-page__form__group form-control form-control--light">
                        <label class="contact-page__form_label form-control__label">{{ $t('contactPage.labelEmail') }}</label>
                        <input 
                            type="text" 
                            class="contact-page__form__form-control form-control__input" 
                            v-model="formData.email"
                            v-bind:placeholder="$t('contactPage.placeholderEmail')">
                    </div>

                </div>

                <div class="contact-page__form__row">
                    <div class="contact-page__form__group form-control form-control--light form-control--textarea">
                        <label class="contact-page__form_label form-control__label">{{ $t('contactPage.labelMessage') }}</label>
                        <textarea 
                            class="contact-page__form__textarea form-control__input"
                            v-bind:placeholder="$t('contactPage.placeholderMessage')"
                            v-model="formData.message">
                        </textarea>
                    </div>
                </div>

                <div class="contact-page__form__row">
                    <div class="contact-page__form__actions">
                        <button 
                            class="contact-page__form__submit button button--primary-light" 
                            v-on:click="onSubmit($event)">
                                {{ $t('contactPage.submitButton') }}
                        </button>
                    </div>
                </div>

            </form>
            <p class="contact-page__form__success-msg" v-if="sendSuccess">{{ $t('contactPage.sendSuccess') }}</p>
        </div>

        <load-indicator v-if="isLoading"></load-indicator>

    </section>
</template>

<style lang="scss" scoped>
.contact-page {
    color: $color-light;
}
.contact-page__content-wrapper {
    display: block;
    max-width: 68.8rem;
    margin: 12.6rem auto;

    @include breakpoint('md-down') {
        max-width: 78.4rem;
    }

    @include breakpoint('sm-down') {
        display: block;
        box-sizing: border-box;
        width: 100%;
        max-width: 48rem;
        padding: 7.2rem 1.6rem;
        margin: 0 auto;
    }
}
.contact-page__title {
    max-width: none;
    margin: 5.6rem 0 6.92rem;

    @include breakpoint('sm-down') {
        margin: 1.6rem 2.4rem 4rem 0;
    }
}
.contact-page__intro {
    font-size: 1.8rem;
    line-height: 1.3;
    margin: 0 0 4.8rem;
}
.contact-page__form__row {
    display: flex;
    margin: 1.6rem -1.6rem;

    @include breakpoint('sm-down') {
        display: block;
    }
}
.contact-page__form__group,
.contact-page__form__actions {
    width: calc(100% - 3.2rem);
    margin: 0 1.6rem;
}
.contact-page__form__actions {
    margin-top: 4rem;
}
.contact-page__form__textarea {
    height: 19.2rem;
}
.contact-page__form__validation-error,
.contact-page__form__send-error {
    color: $color-danger;
    margin: 0 0 0.8rem;
}
.contact-page__form__validation {
    margin: 0 0 4rem;
    padding: 0;
    color: $color-danger;
}
.contact-page__form__validation-error__field {
    list-style: none;
}
.contact-page__form__success-msg {
    font-size: 2.4rem;
    line-height: 1.3;
    max-width: 47.2rem;
}
</style>
